import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Language, TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { SearchService } from '../../services/search.service';
import { SearchResults } from '../../models/data.models';
import { ThemeService } from '../../services/theme.service';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { BrandLogoComponent } from '../brand-logo/brand-logo.component';
import { RegionalThemeService } from '../../services/regional-theme.service';
import { FeedbackService } from '../../services/feedback.service';

export type SearchResultType = 'event' | 'human' | 'dog';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, TranslatePipe, DisplayValuePipe, RouterModule, BrandLogoComponent],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class HeaderComponent {
  readonly feedback = inject(FeedbackService);
  private translationService = inject(TranslationService);
  private searchService = inject(SearchService);
  private elementRef = inject(ElementRef);
  private themeService = inject(ThemeService);
  private router = inject(Router);
  private authService = inject(AuthService);
  readonly regionalTheme = inject(RegionalThemeService);

  currentLang = this.translationService.currentLang;
  theme = this.themeService.theme;
  isAuthenticated = this.authService.isAuthenticated;
  isAdmin = this.authService.isAdmin;

  isMobileMenuOpen = signal(false);
  isSearchOverlayOpen = signal(false);
  isSearchExpanded = signal(false);
  isMoreMenuOpen = signal(false);
  searchQuery = signal('');
  showSearchResults = signal(false);
  searchResults = signal<SearchResults>({ events: [], teams: [], humans: [] });

  hasResults = computed(() => {
    const results = this.searchResults();
    return results.events.length > 0 || results.teams.length > 0 || results.humans.length > 0;
  });

  readonly primaryViews = [
    {
      path: '/races',
      nameKey: 'header.races',
      iconCode: 'M3 21v-8a2 2 0 012-2h14a2 2 0 012 2v8l-2-2-4 2-4-2-4 2-2-2z'
    },
    {
      path: '/courses',
      nameKey: 'header.courses',
      iconCode: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
    },
    {
      path: '/rankings',
      nameKey: 'header.rankings',
      iconCode: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
    },
    {
      path: '/clubs',
      nameKey: 'header.clubs',
      iconCode: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
    },
    {
      path: '/compare/human',
      nameKey: 'header.versus',
      iconCode: 'M7 7h11m0 0-3-3m3 3-3 3M17 17H6m0 0 3 3m-3-3 3-3'
    },
  ];

  readonly mobilePrimaryViews = this.primaryViews.slice(0, 4);
  readonly navigationViews = this.primaryViews;

  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchVersion = 0;

  constructor() {
    // Search once typing pauses, and only show the answer to the latest query.
    effect(onCleanup => {
      const query = this.searchQuery();
      const version = ++this.searchVersion;
      if (query.length < 2) {
        this.searchResults.set({ events: [], teams: [], humans: [] });
        this.showSearchResults.set(false);
        return;
      }
      this.searchTimer = setTimeout(async () => {
        try {
          const results = await this.searchService.search(query);
          if (version !== this.searchVersion) return;
          this.searchResults.set(results);
          this.showSearchResults.set(true);
        } catch (e) {
          console.error('Search error', e);
        }
      }, 250);
      onCleanup(() => {
        if (this.searchTimer) clearTimeout(this.searchTimer);
      });
    });
  }

  onDocumentClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showSearchResults.set(false);
      this.isSearchExpanded.set(false);
      this.isMoreMenuOpen.set(false);
    }
  }

  toggleMobileMenu() {
    this.isSearchOverlayOpen.set(false);
    this.isMobileMenuOpen.update(value => !value);
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
  }

  toggleSearchOverlay() {
    this.isMobileMenuOpen.set(false);
    this.isSearchOverlayOpen.update(value => !value);
    if (this.isSearchOverlayOpen()) {
      // Focus will be handled by template directive or we can do it manually if needed, 
      // but for now let's just toggle state.
      setTimeout(() => {
        const input = document.getElementById('mobile-search-input');
        if (input) (input as HTMLElement).focus();
      }, 100);
    }
  }

  closeSearchOverlay() {
    this.isSearchOverlayOpen.set(false);
  }

  toggleDesktopSearch() {
    this.isMoreMenuOpen.set(false);
    this.isSearchExpanded.update(value => !value);
  }

  toggleMoreMenu() {
    this.isSearchExpanded.set(false);
    this.showSearchResults.set(false);
    this.isMoreMenuOpen.update(value => !value);
  }

  closeMoreMenu() {
    this.isMoreMenuOpen.set(false);
  }

  setLanguage(lang: Language) {
    this.translationService.setLanguage(lang);
  }

  setTheme(theme: 'light' | 'dark' | 'system') {
    this.themeService.setTheme(theme);
  }

  onSearchInput(event: Event) {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery.set(query);
  }

  onSearchFocus() {
    if (this.searchQuery().length >= 2) {
      this.showSearchResults.set(true);
    }
  }

  selectResult(type: SearchResultType, id: string) {
    this.searchQuery.set('');
    this.showSearchResults.set(false);
    this.isMobileMenuOpen.set(false);
    this.isSearchOverlayOpen.set(false);
    this.isMoreMenuOpen.set(false);

    switch (type) {
      case 'event':
        this.router.navigate(['/race-detail', id]);
        break;
      case 'human':
        this.router.navigate(['/human-profile', id]);
        break;
      case 'dog':
        this.router.navigate(['/dog-profile', id]);
        break;
    }
  }

  async onLogout() {
    this.closeMobileMenu();
    this.closeMoreMenu();
    // AuthService sends the visitor to the login page once signed out.
    await this.authService.signOut();
  }

  @HostListener('document:keydown.escape')
  closeOpenMenus() {
    this.closeMobileMenu();
    this.closeSearchOverlay();
    this.closeMoreMenu();
    this.isSearchExpanded.set(false);
    this.showSearchResults.set(false);
  }

  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth >= 1024) { // lg breakpoint
      this.closeMobileMenu();
      this.closeSearchOverlay();
    }
  }
}
